/**
 * The entry dialog of a supplier invoice or credit note (batch 8 T12c3, #3033).
 *
 * Flow: the user fills the form (euros, exact conversion), asks for a REVIEW (the read-only preview of the server: evaluation, blockers,
 * variances, coverage before and after, optional allocation proposals), and only a review that still matches the document EXACTLY can
 * be saved. Every edit invalidates the review in the same render that sees the edit (the review carries the canonical document it
 * was made for; it is compared at render and again at the moment of saving), and every asynchronous review answer is fenced by the
 * owner, a monotonic edit generation and the canonical current inputs, so an answer for an earlier state of the form (also one
 * that equals the current state again, A-B-A) is dropped. Allocation proposals are the server's suggestion; copying one changes the
 * document and therefore needs a NEW review (a new preview token) before saving. Saving sends exactly the reviewed document, the
 * review's token and one journaled `Idempotency-Key`; after an ambiguous failure only an explicit retry of the same request exists.
 */

import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import {
  useInvoiceFacts,
  useInvoiceOperations,
  useInvoicePreview,
  type AllocationProposal,
  type InvoiceDocumentBody,
  type InvoicePreviewResponse,
  type InvoiceWriteOutcome,
  type OperationBlock,
  type OperationResult,
} from '@/hooks/useSupplierInvoices';
import { InvoiceBoundary, useStillShown } from './InvoiceBoundary';
import { InvoiceLineEditor } from './InvoiceLineEditor';
import { CoverageSummary, EvaluationView_, PendingOperations, Section } from './InvoiceParts';
import { MAX_LINES, applyProposals, buildDocument, canonicalOf, emptyDraft, emptyLine, reconcileLinks, removedStillApplies, type InvoiceDraft, type LineDraft, type RemovedLinks } from './invoiceDraft';
import type { JournalOperation } from './invoiceJournal';
import { isSessionCancel, useInvoiceAccess } from './invoiceSession';
import { NS, StatusChip, ViolationList, poLineLabel, useCodeText, useErrorText, useFormatters, type ErrorText } from './invoiceView';

export interface SupplierInvoiceDialogProps {
  open: boolean;
  /** The purchase order the document is entered for. */
  purchaseOrderId: string;
  onClose: () => void;
  /** Called once with the server's answer after the document was stored (also for a replay). */
  onCreated?: (outcome: InvoiceWriteOutcome) => void;
}

/** Standalone: enforces the owner, rights and purchase order boundary itself. */
export function SupplierInvoiceDialog(props: SupplierInvoiceDialogProps) {
  return (
    <InvoiceBoundary target={props.purchaseOrderId}>
      <EntryDialog {...props} />
    </InvoiceBoundary>
  );
}

interface Review {
  /** The canonical document this review was made for. */
  canonical: string;
  body: InvoiceDocumentBody;
  response: InvoicePreviewResponse;
}

function EntryDialog({ open, purchaseOrderId, onClose, onCreated }: SupplierInvoiceDialogProps) {
  const { t } = useTranslation(NS);
  const { t: tc } = useTranslation('common');
  const access = useInvoiceAccess();
  const stillShown = useStillShown();
  // Facts carry prices: a session that may not match documents asks for none of them.
  const facts = useInvoiceFacts(access.canMatch ? purchaseOrderId : '');
  const ops = useInvoiceOperations(purchaseOrderId, stillShown);
  const previewer = useInvoicePreview();
  const errorText = useErrorText();
  const text = useCodeText();
  const fmt = useFormatters();

  const [draft, setDraft] = useState<InvoiceDraft>(() => emptyDraft());
  const draftRef = useRef(draft);
  /** Monotonic: every edit moves it, so an answer for an earlier state is never taken for the current one (A-B-A). */
  const generation = useRef(0);
  const previewSeq = useRef(0);
  const reviewRef = useRef<Review | null>(null);
  const [review, setReviewState] = useState<Review | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<ErrorText | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<InvoiceWriteOutcome | null>(null);
  const [createError, setCreateError] = useState<{ error: ErrorText; refused: boolean } | null>(null);
  const [blocked, setBlocked] = useState<OperationBlock | null>(null);
  const [proposalCopied, setProposalCopied] = useState(false);
  /** True once this dialog has reported the stored document (first-try success or a successful explicit retry): reported once. */
  const createdRef = useRef(false);
  const [removedLinks, setRemovedLinks] = useState<Record<string, RemovedLinks>>({});

  const setReview = (next: Review | null) => {
    reviewRef.current = next;
    setReviewState(next);
  };

  /** Every change of the form goes through here: the generation moves in the same step, the review is judged against the new text. */
  const edit = (update: (current: InvoiceDraft) => InvoiceDraft) => {
    generation.current += 1;
    draftRef.current = update(draftRef.current);
    setDraft(draftRef.current);
    setProposalCopied(false);
    setBlocked(null);
  };

  const built = useMemo(() => buildDocument(draft), [draft]);
  const canonical = built.ok ? canonicalOf(built.body) : null;
  /** Judged in this render: an edit has already changed `canonical`, so the review stops applying before any effect could run. */
  const reviewCurrent = review !== null && canonical !== null && review.canonical === canonical;

  const poLines = facts.data?.lines ?? [];
  const createPending = ops.operations.some((operation) => operation.kind === 'create');
  const locked = created !== null || createPending || creating;
  const journalOk = ops.journalStatus === 'ok';
  const flowBlocked = facts.data !== undefined && !facts.data.purchaseOrder.lineFlowAllowed;

  const setLine = (id: string, patch: Partial<LineDraft>) => {
    // A new sign of the quantity removes the links of the other kind in the same step as the edit (never hidden, never revived).
    let removed: RemovedLinks | null = null;
    edit((current) => ({
      ...current,
      lines: current.lines.map((line) => {
        if (line.id !== id) return line;
        const next = { ...line, ...patch };
        if (patch.quantity === undefined) return next;
        const reconciled = reconcileLinks(next);
        removed = reconciled.removed;
        return reconciled.line;
      }),
    }));
    if (patch.quantity !== undefined) {
      const quantity = patch.quantity;
      setRemovedLinks((current) => {
        if (removed !== null) return { ...current, [id]: removed };
        const existing = current[id];
        if (existing === undefined || removedStillApplies(existing, quantity)) return current;
        const { [id]: _gone, ...rest } = current;
        return rest;
      });
    }
  };

  const runPreview = async (propose: boolean) => {
    setShowErrors(true);
    const now = buildDocument(draftRef.current);
    if (!now.ok) return;
    const requested = canonicalOf(now.body);
    const gen = generation.current;
    const seq = (previewSeq.current += 1);
    setPreviewing(true);
    setPreviewError(null);
    setCreateError(null);
    try {
      const response = await previewer.preview(purchaseOrderId, { ...now.body, proposeAllocations: propose }, stillShown);
      const latest = buildDocument(draftRef.current);
      const sameInputs = latest.ok && canonicalOf(latest.body) === requested;
      if (!stillShown() || seq !== previewSeq.current || gen !== generation.current || !sameInputs) return;
      setReview({ canonical: requested, body: now.body, response });
    } catch (error) {
      if (isSessionCancel(error) || !stillShown() || seq !== previewSeq.current || gen !== generation.current) return;
      setReview(null);
      setPreviewError(errorText(error));
    } finally {
      if (stillShown() && seq === previewSeq.current) setPreviewing(false);
    }
  };

  const copyProposals = (proposals: readonly AllocationProposal[]) => {
    edit((current) => applyProposals(current, proposals));
    setReview(null);
    setProposalCopied(true);
  };

  /** The one place a stored document is reported, whichever way the answer came (save, or the retry of the pending notice). */
  const finishCreate = (result: InvoiceWriteOutcome) => {
    if (createdRef.current) return;
    createdRef.current = true;
    setCreating(false);
    setCreateError(null);
    setBlocked(null);
    setCreated(result);
    onCreated?.(result);
  };

  /**
   * The answer of an explicit retry of this order's pending create. Only the create of THIS order counts (another operation of the
   * owner must not complete this form), and `success` only arrives while the owner, rights and order still hold (no callback or
   * confirmation for another owner). A refusal ends the held request: nothing was stored, the old review no longer applies.
   */
  const onRetryResult = (result: OperationResult, operation: JournalOperation) => {
    if (!stillShown() || operation.kind !== 'create' || operation.target !== purchaseOrderId || operation.purchaseOrderId !== purchaseOrderId) return;
    if (result.status === 'success') finishCreate(result.result);
    else if (result.status === 'refused') {
      generation.current += 1;
      setCreateError({ error: errorText(result.error), refused: true });
      setReview(null);
    } else if (result.status === 'uncertain') setCreateError({ error: errorText(result.error), refused: false });
  };

  const canSave =
    access.canMatch &&
    journalOk &&
    !locked &&
    !previewing &&
    reviewCurrent &&
    review !== null &&
    review.response.evaluation.ok &&
    review.response.blockers.length === 0;

  const save = async () => {
    setConfirmOpen(false);
    const held = reviewRef.current;
    const latest = buildDocument(draftRef.current);
    // The review must still be the document: an edit while the confirmation was open ends here.
    if (!held || !latest.ok || canonicalOf(latest.body) !== held.canonical) return;
    setCreating(true);
    setCreateError(null);
    setBlocked(null);
    const outcome = await ops.create({ ...held.body, previewToken: held.response.previewToken });
    if (!stillShown()) return;
    setCreating(false);
    if (outcome.status === 'success') {
      finishCreate(outcome.result);
    } else if (outcome.status === 'refused') {
      // The server said no: nothing was stored and nothing is retried. A fresh review is the way on.
      setCreateError({ error: errorText(outcome.error), refused: true });
      setReview(null);
    } else if (outcome.status === 'uncertain') {
      setCreateError({ error: errorText(outcome.error), refused: false });
    } else if (outcome.status === 'blocked') {
      setBlocked(outcome.reason);
    }
  };

  const err = (path: string): { error: boolean; helperText?: string } => {
    const code = showErrors && built.ok === false ? built.errors[path] : undefined;
    return code ? { error: true, helperText: t(`errors.field.${code}`) } : { error: false };
  };
  const errors = built.ok ? {} : built.errors;

  const response = reviewCurrent && review ? review.response : null;
  const orderNumber = facts.data?.purchaseOrder.orderNumber ?? null;

  return (
    <ResponsiveDialog open={open} onClose={onClose} maxWidth="lg" fullWidth mobileTitle={t('entry.title')} aria-labelledby="supplier-invoice-entry-title">
      <DialogTitle id="supplier-invoice-entry-title">
        {t('entry.title')}
        {orderNumber !== null && (
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            {t('entry.forOrder', { number: orderNumber })}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {!access.canMatch ? (
          <Alert severity="info">{t('access.noMatch')}</Alert>
        ) : (
          <Stack spacing={2}>
            {facts.isLoading && (
              <Stack direction="row" spacing={1} alignItems="center" role="status">
                <CircularProgress size={18} />
                <Typography variant="body2">{t('loading.facts')}</Typography>
              </Stack>
            )}
            {facts.isError && (
              <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => facts.refetch()}>{t('retryRead')}</Button>}>
                {errorText(facts.error).message}
              </Alert>
            )}
            {flowBlocked && facts.data && (
              <Alert severity="error" data-testid="flow-blocked">
                <AlertTitle>{t('flow.blockedTitle')}</AlertTitle>
                {t('flow.blockedBody')}
                <ViolationList violations={facts.data.purchaseOrder.lineFlowViolations} />
              </Alert>
            )}
            <PendingOperations ops={ops} onResult={onRetryResult} />
            {blocked && <Alert severity="error">{t(`blocked.${blocked}`)}</Alert>}

            {created ? (
              <Alert severity="success" role="status" data-testid="created">
                <AlertTitle>{t(created.replayed ? 'created.titleReplayed' : 'created.title')}</AlertTitle>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <StatusChip status={created.status} />
                  <Typography variant="body2">{t('created.revision', { revision: created.revision })}</Typography>
                  <Chip size="small" variant="outlined" label={t('evaluation.noStockEffect')} />
                </Stack>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t('created.body')}
                </Typography>
              </Alert>
            ) : (
              facts.data &&
              !flowBlocked && (
                <>
                  <Section title={t('form.headerTitle')}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                      <TextField
                        select
                        size="small"
                        label={t('form.documentKind')}
                        value={draft.documentKind}
                        disabled={locked}
                        onChange={(event) => edit((current) => ({ ...current, documentKind: event.target.value === 'credit_note' ? 'credit_note' : 'invoice' }))}
                        slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
                      >
                        <option value="invoice">{t('form.kind.invoice')}</option>
                        <option value="credit_note">{t('form.kind.credit_note')}</option>
                      </TextField>
                      <TextField size="small" label={t('form.invoiceNumber')} value={draft.invoiceNumber} disabled={locked} onChange={(event) => edit((current) => ({ ...current, invoiceNumber: event.target.value }))} {...(showErrors && errors['invoiceNumber'] ? err('invoiceNumber') : { helperText: t('form.invoiceNumberHint') })} />
                      <TextField
                        size="small"
                        type="date"
                        label={t('form.invoiceDate')}
                        value={draft.invoiceDate}
                        disabled={locked}
                        onChange={(event) => edit((current) => ({ ...current, invoiceDate: event.target.value }))}
                        slotProps={{ inputLabel: { shrink: true } }}
                        {...err('invoiceDate')}
                      />
                      <TextField size="small" label={t('form.currency')} value="EUR" disabled helperText={t('form.currencyHint')} />
                      <TextField size="small" label={t('form.shippingNet')} value={draft.shippingNet} disabled={locked} onChange={(event) => edit((current) => ({ ...current, shippingNet: event.target.value }))} slotProps={{ htmlInput: { inputMode: 'decimal' } }} {...(showErrors && errors['shippingNet'] ? err('shippingNet') : { helperText: t('form.shippingNetHint') })} />
                      <TextField size="small" label={t('form.shippingVat')} value={draft.shippingVat} disabled={locked} onChange={(event) => edit((current) => ({ ...current, shippingVat: event.target.value }))} slotProps={{ htmlInput: { inputMode: 'decimal' } }} {...err('shippingVat')} />
                    </Box>
                  </Section>
                  <Section title={t('form.statedTitle')}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {t('form.statedHint')}
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                      <TextField size="small" label={t('form.statedNetTotal')} value={draft.statedNet} disabled={locked} onChange={(event) => edit((current) => ({ ...current, statedNet: event.target.value }))} slotProps={{ htmlInput: { inputMode: 'decimal' } }} {...err('statedNet')} />
                      <TextField size="small" label={t('form.statedVatTotal')} value={draft.statedVat} disabled={locked} onChange={(event) => edit((current) => ({ ...current, statedVat: event.target.value }))} slotProps={{ htmlInput: { inputMode: 'decimal' } }} {...err('statedVat')} />
                      <TextField size="small" label={t('form.statedGrossTotal')} value={draft.statedGross} disabled={locked} onChange={(event) => edit((current) => ({ ...current, statedGross: event.target.value }))} slotProps={{ htmlInput: { inputMode: 'decimal' } }} {...err('statedGross')} />
                    </Box>
                  </Section>
                  <Section title={t('form.linesTitle')}>
                    <Stack spacing={1.5}>
                      {draft.lines.map((line, index) => (
                        <InvoiceLineEditor
                          key={line.id}
                          line={line}
                          index={index}
                          poLines={poLines}
                          errors={errors}
                          showErrors={showErrors}
                          disabled={locked}
                          canRemove={draft.lines.length > 1}
                          removedLinks={removedLinks[line.id]}
                          onChange={(patch) => setLine(line.id, patch)}
                          onRemove={() => edit((current) => ({ ...current, lines: current.lines.filter((candidate) => candidate.id !== line.id) }))}
                        />
                      ))}
                      {showErrors && errors['lines'] && <Alert severity="error">{t(`errors.field.${errors['lines']}`)}</Alert>}
                      <Box>
                        <Button startIcon={<AddIcon />} disabled={locked || draft.lines.length >= MAX_LINES} onClick={() => edit((current) => ({ ...current, lines: [...current.lines, emptyLine()] }))}>
                          {t('form.addLine')}
                        </Button>
                      </Box>
                    </Stack>
                  </Section>
                  {showErrors && !built.ok && <Alert severity="error">{t('form.fixErrors')}</Alert>}
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button variant="outlined" disabled={locked || previewing || !journalOk} onClick={() => void runPreview(false)}>
                      {previewing ? tc('loading') : t('review.run')}
                    </Button>
                    <Button variant="text" disabled={locked || previewing || !journalOk} onClick={() => void runPreview(true)}>
                      {t('review.runWithProposals')}
                    </Button>
                  </Stack>
                  {previewError && (
                    <Alert severity="error" data-testid="preview-error">
                      {previewError.message}
                      {previewError.violations.length > 0 && <ViolationList violations={previewError.violations} />}
                    </Alert>
                  )}
                  {proposalCopied && !review && (
                    <Alert severity="info" role="status" data-testid="proposal-copied">
                      {t('review.proposalCopied')}
                    </Alert>
                  )}
                  {review && !reviewCurrent && (
                    <Alert severity="warning" role="status" data-testid="review-outdated">
                      {t('review.outdated')}
                    </Alert>
                  )}
                </>
              )
            )}

            {response && review && facts.data && (
              <Section title={t('review.title')}>
                <Stack spacing={2} data-testid="review">
                  {response.blockers.length > 0 && (
                    <Alert severity="error" data-testid="blockers">
                      <AlertTitle>{t('review.blockersTitle')}</AlertTitle>
                      <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                        {response.blockers.map((blocker) => (
                          <li key={blocker.reason}>{text('blocker', blocker.reason)}</li>
                        ))}
                      </ul>
                    </Alert>
                  )}
                  <EvaluationView_ evaluation={response.evaluation} poLines={poLines} />
                  {response.proposals !== null && (
                    <ProposalBlock
                      proposals={response.proposals}
                      violations={response.proposalViolations}
                      withProposal={response.evaluationWithProposal}
                      poLines={poLines}
                      disabled={locked}
                      onCopy={() => copyProposals(response.proposals ?? [])}
                    />
                  )}
                  <Divider />
                  <Typography variant="subtitle2">{t('review.coverageTitle')}</Typography>
                  {response.coverageAfter ? (
                    <CoverageSummary
                      coverage={response.coverageBefore}
                      compareWith={response.coverageAfter}
                      poLines={poLines}
                      compareLabels={{ current: t('review.before'), other: t('review.after') }}
                    />
                  ) : (
                    <CoverageSummary coverage={response.coverageBefore} poLines={poLines} />
                  )}
                </Stack>
              </Section>
            )}

            {createError && (
              <Alert severity={createError.refused ? 'error' : 'warning'} data-testid="create-error">
                <AlertTitle>{createError.refused ? t('create.refusedTitle') : t('create.uncertainTitle')}</AlertTitle>
                {createError.error.message}
                {createError.error.violations.length > 0 && <ViolationList violations={createError.error.violations} />}
                {createError.refused && <Typography variant="body2">{t('create.reviewAgain')}</Typography>}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{created ? tc('close') : tc('cancel')}</Button>
        {!created && access.canMatch && (
          <Button variant="contained" disabled={!canSave} onClick={() => setConfirmOpen(true)}>
            {creating ? tc('loading') : t('create.save')}
          </Button>
        )}
      </DialogActions>
      <ConfirmDialog
        open={confirmOpen}
        title={t('create.confirmTitle')}
        message={
          response && response.evaluation.ok
            ? t('create.confirmMessage', {
                kind: t(`form.kind.${draft.documentKind}`),
                number: draft.invoiceNumber.trim(),
                status: text('status', response.evaluation.result.proposedStatus),
                gross: built.ok ? fmt.cents(built.body.stated.grossTotalCents) : '',
              })
            : ''
        }
        confirmText={t('create.confirmSave')}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => void save()}
      />
    </ResponsiveDialog>
  );
}

function ProposalBlock({
  proposals,
  violations,
  withProposal,
  poLines,
  disabled,
  onCopy,
}: {
  proposals: readonly AllocationProposal[];
  violations: readonly { code: string; message: string }[];
  withProposal: InvoicePreviewResponse['evaluationWithProposal'];
  poLines: readonly { id: string; lineNumber: number; partSku: string | null; partName: string | null; receiptLines: readonly { id: string; receiptNumber: string | null }[] }[];
  disabled: boolean;
  onCopy: () => void;
}) {
  const { t } = useTranslation(NS);
  const text = useCodeText();
  const byId = new Map(poLines.map((line) => [line.id.toLowerCase(), line] as const));
  const receiptName = (receiptLineId: string): string => {
    for (const line of poLines) {
      const receipt = line.receiptLines.find((candidate) => candidate.id.toLowerCase() === receiptLineId.toLowerCase());
      if (receipt) return receipt.receiptNumber ?? t('form.unnumbered');
    }
    return t('form.unnumbered');
  };
  return (
    <Alert severity="info" icon={false} data-testid="proposals">
      <AlertTitle>{t('proposals.title')}</AlertTitle>
      <Typography variant="body2" gutterBottom>
        {t('proposals.optional')}
      </Typography>
      {violations.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
            {violations.map((violation) => (
              <li key={violation.code}>{text('violation', violation.code)}</li>
            ))}
          </ul>
        </Alert>
      )}
      {proposals.length === 0 && violations.length === 0 && <Typography variant="body2">{t('proposals.none')}</Typography>}
      <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
        {proposals.map((proposal) => (
          <li key={proposal.lineNumber}>
            {t('proposals.line', {
              number: proposal.lineNumber,
              poLine: poLineLabel(byId.get(proposal.purchaseOrderLineId.toLowerCase()), t),
              quantity: proposal.requestedQuantity,
            })}{' '}
            {proposal.allocations.map((allocation) => t('proposals.allocation', { receipt: receiptName(allocation.receiptLineId), quantity: allocation.quantity })).join(', ')}
            {proposal.shortfall > 0 && ` ${t('proposals.shortfall', { count: proposal.shortfall })}`}
          </li>
        ))}
      </ul>
      {withProposal && withProposal.ok && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          {t('proposals.wouldBe', { status: text('status', withProposal.result.proposedStatus) })}
        </Typography>
      )}
      {withProposal && !withProposal.ok && <Typography variant="body2" sx={{ mt: 1 }}>{t('proposals.wouldNotEvaluate')}</Typography>}
      {proposals.some((proposal) => proposal.allocations.length > 0) && (
        <Box sx={{ mt: 1 }}>
          <Button size="small" variant="outlined" disabled={disabled} onClick={onCopy}>
            {t('proposals.use')}
          </Button>
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('proposals.useNote')}
          </Typography>
        </Box>
      )}
    </Alert>
  );
}
